import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";
import { mainSiteUrl, categories } from "@/mocks/categories";

const navLinks = [
  { key: "nav.home", to: "/" },
  { key: "nav.categories", to: "/#categories" },
  { key: "nav.guides", to: "/#guides" },
  { key: "nav.faq", to: "/#faq" },
];

export default function Navbar({ solid = false }: { solid?: boolean }) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onDark = !scrolled && !solid;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors ${
        scrolled || solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 w-full max-w-[1280px] items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-line text-lg"></i>
          </span>
          <span
            className={`font-heading text-lg font-bold ${
              onDark ? "text-background-50" : "text-foreground-950"
            }`}
          >
            {t("nav.brand")}
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-full px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                onDark
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {t(link.key)}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-2 md:flex">
          <LanguageSwitcher onDark={onDark} />
          <Link
            to="/"
            className={`rounded-full border px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
              onDark
                ? "border-background-50/50 text-background-50 hover:bg-background-50/15"
                : "border-background-300 text-foreground-800 hover:bg-background-100"
            }`}
          >
            {t("nav.home")}
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            {t("nav.shoppingGuide")}
          </a>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher onDark={onDark} />
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`flex h-10 w-10 items-center justify-center rounded-lg border cursor-pointer transition-colors ${
              onDark
                ? "border-background-50/40 text-background-50"
                : "border-background-300/70 text-foreground-800"
            }`}
            aria-label="Menu"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`}></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">
              {t("categoriesSection.title")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                >
                  <img
                    src={cat.image}
                    alt={t(cat.nameKey)}
                    className="h-7 w-7 flex-shrink-0 rounded-md object-cover object-top"
                  />
                  <span className="truncate">{t(cat.nameKey)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 border-t border-background-200 pt-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="rounded-full bg-primary-500 px-4 py-2.5 text-center text-sm font-semibold text-background-50"
            >
              {t("nav.shoppingGuide")}
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="rounded-full border border-background-300 px-4 py-2.5 text-center text-sm font-semibold text-foreground-800"
            >
              {t("main.buyNow")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}